/**
 * DebugExercisesBlock — module-page wrapper that lists the natural-language
 * debug exercises for a module and renders a DebugFeedbackBlock for each.
 *
 * Mirrors AgenticQuizBlock's contract: takes only `slug` and renders nothing
 * when the module has no exercises or the list call fails (locked / 404).
 * The list endpoint returns [] when locked, so the block stays silently
 * absent without special-casing errors.
 */

import React from "react";
import { Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { DebugExerciseSummary, listDebugExercises } from "../api/client";
import DebugFeedbackBlock from "./DebugFeedbackBlock";

interface DebugExercisesBlockProps {
  slug: string;
}

export default function DebugExercisesBlock({ slug }: DebugExercisesBlockProps) {
  const { data } = useQuery<DebugExerciseSummary[]>({
    queryKey: ["debug-exercises", slug],
    queryFn: () => listDebugExercises(slug),
    retry: false,
  });

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {data.map((ex) => (
        <DebugFeedbackBlock
          key={ex.exercise_id}
          slug={slug}
          exerciseId={ex.exercise_id}
        />
      ))}
    </Stack>
  );
}
